import { useState, FormEvent } from 'react';

export default function ConsultationForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value as string));

    setStatus('submitting');
    try {
      const res = await fetch('https://readdy.ai/api/form/d6pep6k4k19g20dvqq30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="relative bg-[#1A1C1E] py-24 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(#00E5FF 1px, transparent 1px), linear-gradient(90deg, #00E5FF 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#F4F7F6] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Schedule a Systems Consultation
          </h2>
          <p className="text-[#7F8C8D] text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            Tell us about your business and we&apos;ll design an automation roadmap tailored to your operations.
          </p>
        </div>

        {status === 'success' ? (
          <div className="text-center py-16 space-y-4">
            <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/10">
              <i className="ri-check-line text-3xl text-[#00E5FF]"></i>
            </div>
            <h3 className="text-2xl font-bold text-[#F4F7F6]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Consultation Request Received
            </h3>
            <p className="text-[#7F8C8D]" style={{ fontFamily: 'Inter, sans-serif' }}>
              Our team will review your submission and reach out within 1–2 business days.
            </p>
          </div>
        ) : (
          <form
            data-readdy-form
            id="consultation-form"
            onSubmit={handleSubmit}
            className="bg-[#111315] border border-[#7F8C8D]/20 rounded-xl p-8 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[#F4F7F6] text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Full Name <span className="text-[#00E5FF]">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  required
                  placeholder="Jane Smith"
                  className="w-full bg-[#1A1C1E] border border-[#7F8C8D]/20 rounded-md px-4 py-3 text-sm text-[#F4F7F6] placeholder-[#7F8C8D]/50 focus:outline-none focus:border-[#00E5FF]/60 transition-colors duration-200"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[#F4F7F6] text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Email Address <span className="text-[#00E5FF]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="jane@company.com"
                  className="w-full bg-[#1A1C1E] border border-[#7F8C8D]/20 rounded-md px-4 py-3 text-sm text-[#F4F7F6] placeholder-[#7F8C8D]/50 focus:outline-none focus:border-[#00E5FF]/60 transition-colors duration-200"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[#F4F7F6] text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Company Name
                </label>
                <input
                  type="text"
                  name="company_name"
                  placeholder="Acme Corp"
                  className="w-full bg-[#1A1C1E] border border-[#7F8C8D]/20 rounded-md px-4 py-3 text-sm text-[#F4F7F6] placeholder-[#7F8C8D]/50 focus:outline-none focus:border-[#00E5FF]/60 transition-colors duration-200"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[#F4F7F6] text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Industry
                </label>
                <select
                  name="industry"
                  className="w-full bg-[#1A1C1E] border border-[#7F8C8D]/20 rounded-md px-4 py-3 text-sm text-[#F4F7F6] focus:outline-none focus:border-[#00E5FF]/60 transition-colors duration-200 cursor-pointer"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="">Select your industry</option>
                  <option value="Financial Services & Lending">Financial Services &amp; Lending</option>
                  <option value="Healthcare & Med Spas">Healthcare &amp; Med Spas</option>
                  <option value="Clinical Healthcare Practices">Clinical Healthcare Practices</option>
                  <option value="Professional Services & Law">Professional Services &amp; Law</option>
                  <option value="Real Estate & Property Development">Real Estate &amp; Property Development</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[#F4F7F6] text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                Describe Your Operational Challenges
              </label>
              <textarea
                name="operational_challenges"
                rows={4}
                maxLength={500}
                placeholder="Tell us about the repetitive tasks, bottlenecks, or manual processes your team deals with daily..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full bg-[#1A1C1E] border border-[#7F8C8D]/20 rounded-md px-4 py-3 text-sm text-[#F4F7F6] placeholder-[#7F8C8D]/50 focus:outline-none focus:border-[#00E5FF]/60 transition-colors duration-200 resize-none"
                style={{ fontFamily: 'Inter, sans-serif' }}
              ></textarea>
              <p className="text-right text-xs text-[#7F8C8D]" style={{ fontFamily: 'Inter, sans-serif' }}>
                {charCount}/500
              </p>
            </div>

            {status === 'error' && (
              <p className="text-red-400 text-sm text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                Something went wrong. Please try again.
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full py-4 bg-[#00E5FF] text-[#1A1C1E] font-semibold rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {status === 'submitting' ? 'Submitting...' : 'Submit Consultation Request'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
